import { useSeo } from '@/lib/seo';
import Header from '@/components/Header';
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Truck, AlertTriangle, Waypoints, Ruler, Clock, ShieldCheck } from 'lucide-react';

const shortKey = (k) => `${k.replace('N-', '').replace('S-', '')} · ${k[0] === 'N' ? 'N' : 'S'}`;
const SNAKE_COLORS = ['#F7941D', '#2DD4B0', '#E11D48', '#6366F1', '#0EA5E9', '#D946EF', '#84CC16', '#F59E0B'];

function loadPath(l) {
  if (l.geometry && l.geometry.length > 1) return l.geometry;
  if (l.origin_lat != null && l.dest_lat != null) return [[l.origin_lat, l.origin_lng], [l.dest_lat, l.dest_lng]];
  return null;
}

function fmtDay(d) {
  try {
    const dt = new Date(d + 'T00:00:00');
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const tom = new Date(today); tom.setDate(tom.getDate() + 1);
    if (dt.getTime() === today.getTime()) return 'Today';
    if (dt.getTime() === tom.getTime()) return 'Tomorrow';
    return dt.toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return d; }
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points && points.length > 1) {
      try { map.fitBounds(L.latLngBounds(points), { padding: [30, 30] }); } catch (e) { /* noop */ }
    }
  }, [points, map]);
  return null;
}

function OverviewMap({ loads }) {
  const routes = loads.map((l, i) => ({ l, i, path: loadPath(l) })).filter((r) => r.path);
  const all = routes.flatMap((r) => r.path);
  const lat = all.length ? all.reduce((s, p) => s + p[0], 0) / all.length : -41.2;
  const lng = all.length ? all.reduce((s, p) => s + p[1], 0) / all.length : 172.5;
  return (
    <div className="card p-0 overflow-hidden mb-8" data-testid="oversize-overview-map">
      <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
        <Waypoints className="w-4 h-4 text-orange-500" />
        <span className="font-display font-bold text-sm">Upcoming oversize loads on the network</span>
        <span className="text-zinc-400 text-xs">· {routes.length} route{routes.length !== 1 ? 's' : ''}</span>
      </div>
      <div style={{ height: 420 }} className="relative">
        {routes.length === 0 && (
          <div className="absolute inset-0 z-[500] flex items-center justify-center bg-white/70 pointer-events-none">
            <span className="text-sm text-zinc-500" data-testid="oversize-no-loads">No oversize loads scheduled right now.</span>
          </div>
        )}
        <MapContainer center={[lat, lng]} zoom={6} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
          <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" attribution="&copy; OpenStreetMap &copy; CARTO" />
          <FitBounds points={all} />
          {routes.map(({ l, i, path }) => {
            const color = SNAKE_COLORS[i % SNAKE_COLORS.length];
            return (
              <div key={l.id}>
                <Polyline positions={path} pathOptions={{ color, weight: 5, opacity: 0.85 }} data-testid={`oversize-snake-${l.id}`}>
                  <Popup>
                    <div className="text-xs" style={{ minWidth: 170 }}>
                      <div className="font-bold" style={{ color }}>{l.title}</div>
                      <div className="mt-0.5">{l.origin} → {l.destination}</div>
                      <div className="text-zinc-500 mt-0.5">{fmtDay(l.date)} · {l.direction}</div>
                    </div>
                  </Popup>
                </Polyline>
                {path.length > 0 && <CircleMarker center={path[0]} radius={5} pathOptions={{ color: '#fff', weight: 2, fillColor: color, fillOpacity: 1 }} />}
                {path.length > 1 && <CircleMarker center={path[path.length - 1]} radius={5} pathOptions={{ color: '#fff', weight: 2, fillColor: color, fillOpacity: 1 }} />}
              </div>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}

function PublicLoadCard({ l }) {
  const wps = (l.waypoints || []).filter((w) => w.eta);
  const dims = [l.width_m && `W ${l.width_m}m`, l.height_m && `H ${l.height_m}m`, l.length_m && `L ${l.length_m}m`, l.weight_t && `${l.weight_t}t`].filter(Boolean).join(' · ');
  return (
    <div className="card" data-testid={`oversize-card-${l.id}`}>
      <div className="flex items-center flex-wrap gap-2">
        <span className="chip chip-outline-orange text-xs uppercase">{fmtDay(l.date)}</span>
        <span className="font-display font-bold text-lg">{l.title}</span>
        <span className="chip chip-outline-teal text-[11px] uppercase">{l.direction || 'other'}</span>
      </div>
      <div className="text-sm text-zinc-600 mt-1">{l.origin} → {l.destination}</div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {(l.highway_keys || []).map((k) => <span key={k} className="chip chip-outline-teal text-[11px]">{shortKey(k)}</span>)}
      </div>
      {dims && (
        <div className="flex items-center gap-2 mt-2 text-xs text-zinc-500"><Ruler className="w-3.5 h-3.5" /> {dims}</div>
      )}
      {wps.length > 0 && (
        <div className="mt-3 overflow-x-auto" data-testid={`oversize-timeline-${l.id}`}>
          <div className="flex items-start gap-0 min-w-max pt-1">
            {wps.map((w, i) => (
              <div key={i} className="flex items-center">
                <div className="flex flex-col items-center px-1">
                  <div className="w-3 h-3 rounded-full bg-orange-500 ring-4 ring-orange-100" />
                  <span className="text-[11px] font-semibold mt-1 inline-flex items-center gap-0.5"><Clock className="w-3 h-3" />{w.eta}</span>
                  <span className="text-[11px] text-zinc-500 max-w-[90px] text-center leading-tight">{w.name}</span>
                </div>
                {i < wps.length - 1 && <div className="h-0.5 w-10 bg-orange-200 mt-1.5" />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function OversizeLoads() {
  useSeo({ title: 'Oversize Load Pilots & Movements', description: 'Oversize and over-dimension load-pilot scheduling and upcoming movements across New Zealand highways.' });
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/loads/public/upcoming').then((r) => setLoads(r.data || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      {/* Hero */}
      <section className="bg-neutral-950 text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/15 border border-orange-500/30 text-orange-300 text-xs tracking-[0.2em] uppercase font-semibold mb-5" data-testid="oversize-badge">
            <Truck className="w-4 h-4" /> Public oversize-load watch
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl leading-tight mb-4">
            Know about the <span className="text-orange-400">oversize load</span> before you meet it.
          </h1>
          <p className="text-white/80 text-lg max-w-2xl leading-relaxed">
            Driving a truck, towing wide, or just heading over a pass? These are the planned oversize and
            over-dimension loads moving on NZ highways over the next few weeks — with routes, directions and
            timings — so you can plan your run and avoid getting stuck nose-to-nose on a narrow stretch.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/signup" className="btn btn-primary" data-testid="oversize-cta-signup">Get free alerts on your route</Link>
            <Link to="/alerts" className="btn btn-secondary">See live road alerts</Link>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 md:px-6 py-10">
        {loading ? (
          <div className="card text-zinc-500">Loading upcoming loads…</div>
        ) : (
          <>
            <OverviewMap loads={loads} />
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-xl">Scheduled loads ({loads.length})</h2>
              <span className="text-xs text-zinc-500">Next 3 weeks · data published by registered load pilots</span>
            </div>
            {loads.length === 0 ? (
              <div className="card text-center py-14">
                <Truck className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
                <h3 className="font-display font-bold text-xl mb-1">No oversize loads scheduled right now</h3>
                <p className="text-zinc-500">Check back soon — pilots publish loads here as they're planned.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {loads.map((l) => <PublicLoadCard key={l.id} l={l} />)}
              </div>
            )}

            {/* Pilot CTA */}
            <div className="card mt-10 border-2 border-orange-200 bg-orange-50/50 flex flex-col sm:flex-row items-center gap-4" data-testid="oversize-pilot-cta">
              <div className="w-12 h-12 rounded-2xl bg-orange-500/15 text-orange-600 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-7 h-7" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <div className="font-display font-black text-lg">Are you a load pilot or transport operator?</div>
                <p className="text-sm text-zinc-600">Publish your planned moves free — auto-build corridors, get head-on meet warnings, and let other drivers plan around you.</p>
              </div>
              <Link to="/signup" className="btn btn-primary whitespace-nowrap" data-testid="oversize-pilot-signup">Register as a Load Pilot</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
